import { ErrorView } from "common/components/error";
import { FetchErrorRetry } from "common/helpers/errors";
import { Component } from "react";
import debounce from "lodash-es/debounce";
import { Highcharts } from "extensions/highcharts";

import {
    HighchartsChart,
    Chart,
    YAxis,
    XAxis,
    BarSeries,
    HighchartsProvider,
    Tooltip
} from "react-jsx-highcharts";

import { toHex } from "common/colors";

import { Loader } from "common/helpers/HorizontalLoader";
import { references } from "common/helpers/references";
import { fetchChartData, drillThrough } from "../widgetHelper";

export class DoublePivotBarChart extends Component {
    static processData(data) {
        // sort current date range dataset
        data[0].sort((a, b) => a.chargebacks_count < b.chargebacks_count);
        const topRecords = data[0].slice(0, 5);
        const topKeys = topRecords.map((r) => r.reason_code);
        
        const theRestArray = data[0] && data[0].length ? data[0].slice(5).map((r) => r.chargebacks_count) : [];
        const theRest = theRestArray && theRestArray.length ? theRestArray.reduce((total, value) => total + value) : 0;
        const matchingRecords = [];
        const nonMatchinRecords = [];
        data[1].forEach((r) => {
            if (topKeys.includes(r.reason_code)) {
                matchingRecords.push(r);
            } else {
                nonMatchinRecords.push(r);
            }
        });
        
        const current = topRecords.map((r) => r.chargebacks_count);
        const prev = matchingRecords.map((r) => r.chargebacks_count);
        const nonMatchingTotalArray = nonMatchinRecords.map((r) => r.chargebacks_count);
        const nonMatchingTotal = nonMatchingTotalArray && nonMatchingTotalArray.length ? nonMatchingTotalArray.reduce((total, value) => total + value) : 0;
        current.push(theRest);
        prev.push(nonMatchingTotal);
        topKeys.push("Others");
        return { keys: topKeys, current, prev };
    }
    
    constructor(props) {
        super();
        this.state = {
            loaded: false,
            error: false
        };
        this.resizeMethod = debounce(this.reflowChart.bind(this), 300);
    }
    
    componentDidMount() {
        if (this.existingPromiseRequest) {
            this.existingPromiseRequest.cancel();
        }
        
        this.existingPromiseRequest = Promise.map(this.props.dateRanges, (r) => fetchChartData(r, this.props))
            .then((data) => {
                const processed = DoublePivotBarChart.processData(data);
                this.setState({
                    loaded: true,
                    error: false,
                    data: processed
                });
            }, (error) => {
                this.setState({
                    loaded: true,
                    error: true
                });
            });
        
        window.addEventListener("resize", this.resizeMethod);
    }
    
    componentWillUnmount() {
        // Cancel any pending debounce func
        this.resizeMethod.cancel();
        window.removeEventListener("resize", this.resizeMethod);
        
        if (this.existingPromiseRequest) {
            this.existingPromiseRequest.cancel();
        }
    }
    
    getChart = (chart) => {
        this.chart = chart;
    };
    
    getAPIEndpoint() {
        const endpoint = this.props.subModel.get("api_endpoint");
        const pivot = this.props.second_pivot_key;
        
        return typeof endpoint === "function" ? endpoint(pivot) : endpoint;
    }
    
    getAvailableHeight() {
        const container = this.props.widgetContainer;
        const headerH = container.querySelector(".widgetTitle").clientHeight;
        const tabsH = container.querySelector(".nav-tabs").clientHeight;
        const summaryBars = Array.from(container.querySelectorAll(".summary-bar")).find((sb) => sb.clientHeight > 0);
        const summaryH = summaryBars.clientHeight;
        
        return container.clientHeight - (headerH + tabsH + summaryH);
    }
    
    reflowChart() {
        if (this.chart) {
            // this.chart.reflow();
            this.chart.setSize(null, this.getAvailableHeight());
        }
    }
    
    drillDown(e, category) {
        if (category !== "Others") {
            drillThrough(this.props, category);
        }
    }
    
    render() {
        const self = this;
        if (this.state.error) {
            return (
                <ErrorView
                    captureInSentry={false}
                    error={new FetchErrorRetry({
                        message: "Please refresh widget as data could not load",
                        customActions: [{
                            className: "retry-widget",
                            label: "Refresh widget"
                        }],
                        hideDetails: true,
                        custom: true
                    })}
                />
            );
        }
        
        if (this.state.loaded) {
            const myTabName = references.toAlias(self.props.pivotName, self.props.first_pivot_key);
            const height = this.getAvailableHeight();
            const maxYaxis = Math.round(Math.max(...this.state.data.current.slice(0, 5), ...this.state.data.prev.slice(0, 5)));
            const tickInterval = maxYaxis ? Math.round((maxYaxis * 0.85) / 3) : null;
            const barsPastAxis = (this.state.data.current[5] > maxYaxis) || (this.state.data.prev[5] > maxYaxis);
            const tooltipProps = {
                tooltip: {
                    formatter(tooltip) {
                        if (this.point.isNull) {
                            return "Null";
                        }
                        // If not null, use the default formatter
                        return tooltip.defaultFormatter.call(this, tooltip);
                    }
                }
            };
            
            const xAxisProps = {
                categories: this.state.data.keys,
                labels: {
                    useHTML: true,
                    formatter() {
                        const description = references.toAlias(this.value, self.props.second_pivot_key);
                        const content = `<div class='doublePivot-tooltip'><b>${myTabName} chargeback code ${this.value}</b><p>${description}</p></div>`;
                        return `<span class="pz-popover" role="button" data-html="true" data-toggle="popover" data-trigger="hover" data-placement="top" data-content="${content}" data-original-title="" title="" />${this.value}</span>`;
                    }
                }
            };
            
            const yAxisProps = {
                min: 0,
                max: maxYaxis,
                tickInterval,
                labels: {
                    formatter() {
                        if (this.isLast && barsPastAxis) {
                            return `${this.value} +`;
                        }
                        return this.value;
                    },
                    overflow: "justify"
                },
                lineColor: toHex("paz-black-l-30"),
                lineWidth: 1
            };
            
            const seriesProps = {
                cursor: "pointer",
                point: {
                    events: {
                        click(e) {
                            self.drillDown(e, this.category);
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    },
                    hover: {
                        brightness: -0.2 // darken
                    }
                }
            };
            
            return (
                <div className="chartArea">
                    <HighchartsProvider Highcharts={Highcharts}>
                        <HighchartsChart callback={this.getChart} >
                            <Chart reflow="true" height={height} marginTop={10} marginRight={30} backgroundColor="transparent"/>
                            <Tooltip {...tooltipProps} />
                            <YAxis {...yAxisProps} />
                            <XAxis {...xAxisProps}>
                                <BarSeries {...seriesProps} name="Current period" data={this.state.data.current} color={toHex("wcag-blue")}/>
                                <BarSeries {...seriesProps} name="Prior period" data={this.state.data.prev} color={toHex("paz-black-l-30")}/>
                            </XAxis>
                        </HighchartsChart>
                    </HighchartsProvider>
                </div>
            );
        }
        const loaderStyle = {
            paddingTop: "100px",
            width: "100%"
        };
        const style = {
            width: "100%",
            height: "100%"
        };
        
        return (<div style={style} className="chartArea"><Loader style={loaderStyle} loaded={this.state.loaded} /></div>);
    }
}
